import { PenLine } from "lucide-react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAmountInputControlled } from "@/shared/components/forms/ColocaAmountInput";
import { ColocaPopover } from "@/shared/components/overlays/ColocaPopover";
import useTranslations from "@/shared/lang/useTranslations";

import { defaultExampleAmount } from "../../constants/paymentFees.constants";
import { formatAmountText } from "../../utils/feeFormat";

export interface ExampleAmountsBarProps {
  currencies: string[];
  exampleAmountFor: (currency: string) => string;
  onChange: (currency: string, value: string) => void;
  onReset: (currency: string) => void;
}

export function ExampleAmountsBar({
  currencies,
  exampleAmountFor,
  onChange,
  onReset,
}: ExampleAmountsBarProps) {
  const { t, activeLanguage } = useTranslations("paymentFees");
  if (currencies.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-sm">
      <span className="text-muted-foreground">{t("examples_label")}</span>
      {currencies.map((currency) => {
        const amount = exampleAmountFor(currency);
        const isDefault = amount === defaultExampleAmount(currency);
        return (
          <ColocaPopover
            key={currency}
            align="start"
            contentClassName="w-64"
            title={t("examples_popover_title", { currency })}
            description={t("examples_popover_hint")}
            trigger={
              <ColocaButton
                variant="default"
                size="xs"
                iconTrailing={PenLine}
                aria-label={t("examples_edit_aria", { currency })}
                className="font-mono tabular-nums"
              >
                {formatAmountText(amount || "0", activeLanguage)} {currency}
              </ColocaButton>
            }
          >
            <div className="space-y-2">
              <ColocaAmountInputControlled
                value={amount}
                onValueChange={(value) => onChange(currency, value)}
                suffix={currency}
                maxFractionDigits={8}
                groupThousands
              />
              {!isDefault && (
                <ColocaButton variant="ghost" size="xs" onClick={() => onReset(currency)}>
                  {t("examples_reset")}
                </ColocaButton>
              )}
            </div>
          </ColocaPopover>
        );
      })}
    </div>
  );
}
